import { useParams } from 'react-router-dom';

import { useOutputs, usePrenotas, useSignals, useStoryCandidates, useXReviews } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { ClaimIdChip } from 'src/components/editorial/ClaimIdChip';
import { DiagnosticList } from 'src/components/editorial/DiagnosticList';
import { Badge } from 'src/components/ui/Badge';
import { Disclosure } from 'src/components/ui/Disclosure';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { Skeleton } from 'src/components/ui/Skeleton';
import { indexNormalizedClaims } from 'src/lib/claimThread';
import { PRENOTA_STATUS } from 'src/lib/diagnostics';
import { buildScoreIndex, observedSocialFloor, storyGate, type GateStep } from 'src/lib/storyGate';
import { formatClock } from 'src/lib/time';
import type { EditorialNormalizedClaim, EditorialPrenota } from 'src/types/editorial';

const CATEGORY_LABEL: Record<string, string> = {
  politics_government: 'Política y gobierno',
  economy_business: 'Economía y negocios',
  security_justice: 'Seguridad y justicia',
  health: 'Salud',
  education: 'Educación',
  environment: 'Medio ambiente',
  infrastructure_mobility: 'Infraestructura y movilidad',
  science_technology: 'Ciencia y tecnología',
  society_human_rights: 'Sociedad y derechos humanos',
  international: 'Internacional',
  sports: 'Deportes',
  culture_entertainment: 'Cultura y entretenimiento',
  other: 'Otros',
};

const EVENT_TYPE_LABEL: Record<string, string> = {
  announcement: 'Anuncio',
  decision: 'Decisión',
  proposal: 'Propuesta',
  statement: 'Declaración',
  accusation: 'Acusación',
  denial_or_response: 'Negación o respuesta',
  investigation: 'Investigación',
  legal_action: 'Acción legal',
  incident: 'Incidente',
  appointment_or_resignation: 'Nombramiento o renuncia',
  agreement: 'Acuerdo',
  data_release: 'Publicación de datos',
  protest: 'Protesta',
  other: 'Otro',
};

/**
 * Answers: what story does the machine think this is?
 *
 * The landmark is the story sheet with its claim-chip band — the visible link from a headline back
 * to every claim that backs it.
 */
export function StoriesWorkspace() {
  const { streamId = '' } = useParams<{ streamId: string }>();
  const prenotas = usePrenotas(streamId);
  const candidates = useStoryCandidates(streamId);
  const signals = useSignals(streamId);
  const xReviews = useXReviews(streamId);
  const outputs = useOutputs(streamId);

  const claimIndex = indexNormalizedClaims(signals.data?.signals ?? []);
  const scores = buildScoreIndex(xReviews.data?.xReviews ?? []);
  const notes = outputs.data?.outputs ?? [];
  const socialFloor = observedSocialFloor(notes, scores);

  // The gate is the sort: what became a nota first, what the pipeline stopped last. Reading the
  // list top to bottom is reading the funnel (Composition §2).
  const stories = [...(prenotas.data?.prenotas ?? [])].sort(
    (a, b) => (scores.get(b.storyId) ?? -1) - (scores.get(a.storyId) ?? -1) || a.storyId.localeCompare(b.storyId)
  );

  // The section shell renders in every state — pending, error, empty — so the page never falls
  // back to a bare panel without its heading.
  return (
    <div className="page-medium">
      <div className="tx-head">
        <h2>Historias preliminares</h2>
        {prenotas.data && <span className="tx-count">{stories.length} historias · ordenadas por relevancia en X</span>}
      </div>
      <p className="claims-lead">
        Cada historia se arma a partir de claims de la transmisión. Revisa su avance y abre el respaldo para ver qué se
        dijo.
      </p>

      {prenotas.isPending ? (
        <Skeleton variant="rows" count={5} />
      ) : prenotas.isError ? (
        <LoadFailure title="No se pudieron cargar las historias" error={prenotas.error} onRetry={prenotas.refetch} />
      ) : stories.length === 0 ? (
        <AbsenceState
          kind="not_reached"
          what="No hay prenotas."
          why="La extracción de prenotas corre después del stream, sobre los claims normalizados."
        />
      ) : (
        stories.map((prenota, index) => (
          <StorySheet
            key={prenota.storyId}
            index={index + 1}
            total={stories.length}
            prenota={prenota}
            claimIndex={claimIndex}
            gate={storyGate(prenota, scores, notes, socialFloor)}
          />
        ))
      )}

      <section className="quiet-tail stories__candidates">
        <Disclosure summary="Candidatos previos del motor de criterios" count={candidates.data?.storyCandidates.length ?? 0}>
          {(candidates.data?.storyCandidates.length ?? 0) > 0 ? (
            <div className="stories__candidate-content">
              <p className="stories__candidate-intro">
                El motor agrupó estos temas mientras la transmisión estaba en curso. Las historias preliminares se
                generan después, con la transmisión completa.
              </p>
              <ul className="dropped">
                {candidates.data?.storyCandidates.map(candidate => (
                  <li key={candidate.storyId}>
                    <span className="mono">{candidate.payload.urgency}</span> {candidate.payload.title_candidate}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="stories__candidate-empty">
              <span className="stories__candidate-empty-mark" aria-hidden="true">0</span>
              <div>
                <h3>Sin candidatos previos</h3>
                <p>
                  El motor no agrupó temas durante la transmisión. Las historias preliminares se generan después,
                  a partir de los claims normalizados; por eso ambos conteos pueden diferir.
                </p>
              </div>
            </div>
          )}
        </Disclosure>
      </section>
    </div>
  );
}

function StorySheet({
  index,
  total,
  prenota,
  claimIndex,
  gate,
}: {
  index: number;
  total: number;
  prenota: EditorialPrenota;
  claimIndex: Map<string, EditorialNormalizedClaim>;
  gate: GateStep[];
}) {
  const payload = prenota.payload;
  const sourceClaimIds = payload.source_claim_ids ?? [];
  const eligibility = payload.eligibility;
  const stopped = gate.find(step => step.state === 'fail');

  return (
    <article className="story" data-stopped={stopped !== undefined} id={`story-${prenota.storyId}`}>
      <div className="story__eyebrow">
        <span>HISTORIA</span>
        {stopped && <span className="story__stop">Se detuvo en {stopped.label}</span>}
        <span className="story__position">{String(index).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
      </div>
      <header className="story__head">
        <h2 className="story__headline">{payload.headline}</h2>
        <div className="story__badges">
          <Badge tone="unresolved" title={PRENOTA_STATUS[payload.status]?.hint}>
            {PRENOTA_STATUS[payload.status]?.label ?? payload.status}
          </Badge>
          {/* Only shown when it disagrees with the status badge — two badges saying the same thing
              in two vocabularies is worse than one. */}
          {eligibility && eligibility.decision !== 'allowed' && (
            <Badge tone="contradicted">No puede generar notas</Badge>
          )}
        </div>
      </header>

      <p className="story__summary reading">{payload.summary}</p>

      {/* The three conditions the pipeline actually applies, in the order it applies them. The
          first failing row is where this story stopped — that is the whole question this screen
          exists to answer. */}
      <ol className="gate">
        {gate.map(step => (
          <li key={step.label} className="gate__step" data-state={step.state}>
            <span className="gate__mark" aria-hidden="true" />
            <span className="gate__label">{step.label}</span>
            <span className="gate__detail">{step.detail}</span>
          </li>
        ))}
      </ol>

      {eligibility && eligibility.blocking_codes.length > 0 && (
        <DiagnosticList codes={eligibility.blocking_codes} title="Por qué está bloqueada" />
      )}

      <ul className="story__meta" aria-label="Datos de la historia">
        <li>{CATEGORY_LABEL[payload.primary_category] ?? formatStoryTag(payload.primary_category)}</li>
        <li>{EVENT_TYPE_LABEL[payload.event_type] ?? formatStoryTag(payload.event_type)}</li>
        <li>{payload.entities.length} {payload.entities.length === 1 ? 'entidad' : 'entidades'}</li>
        <li>
          {payload.evidence_chunk_ids.length}{' '}
          {payload.evidence_chunk_ids.length === 1 ? 'bloque de evidencia' : 'bloques de evidencia'}
        </li>
      </ul>

      <Disclosure summary="Claims que sustentan esta historia" count={sourceClaimIds.length || payload.claims.length}>
        {sourceClaimIds.length > 0 ? (
          <ul className="story__claims">
            {sourceClaimIds.map(claimId => {
              const claim = claimIndex.get(claimId);
              return (
                <li key={claimId}>
                  <ClaimIdChip claimId={claimId} />
                  {claim ? (
                    <span className="story__claim-text">{claim.claim_text}</span>
                  ) : (
                    <span className="story__claim-missing">
                      El claim ya no está en las señales cargadas de esta transmisión.
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <>
            <p className="story__claim-missing">
              Esta prenota no trae <code>source_claim_ids</code>: se generó antes de que el hilo del claim se cerrara en
              el paso 4. Abajo van los claims tal como los guardó la prenota, sin id rastreable.
            </p>
            <ul className="story__claims">
              {payload.claims.map((claim, index) => (
                <li key={index}>
                  {claim.claim_id ? <ClaimIdChip claimId={claim.claim_id} /> : <span className="mono">sin id</span>}
                  <span className="story__claim-text reading">
                    “{claim.claim}” — {claim.speaker} · {formatClock(claim.timestamp)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Disclosure>
    </article>
  );
}

function formatStoryTag(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map(word => word ? word.charAt(0).toLocaleUpperCase('es-MX') + word.slice(1) : '')
    .join(' ');
}
